import {ModelSetOptions} from 'backbone';
import _ from 'underscore';

import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {LinearLayout} from 'molbio/maps/linear/LinearLayout';
import {AaSequenceLinearMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export interface AaSequenceMapTabStateAttributes extends SinglePolymerMapTabStateAttributes<
  AaSequence,
  AaSequenceLinearMapState
> {
  visibilityOptions: VisibilityOptions;
  bufferViewLayout: BufferViewLayout | null;
  layout: LinearLayout;
}

interface AaSequenceMapTabState {
  get<K extends keyof AaSequenceMapTabStateAttributes>(key: K): AaSequenceMapTabStateAttributes[K];
  set<K extends keyof AaSequenceMapTabStateAttributes>(
    attributeName: K,
    value: AaSequenceMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): AaSequenceMapTabState;
  set(obj: Partial<AaSequenceMapTabStateAttributes>): AaSequenceMapTabState;
}

class AaSequenceMapTabState extends SinglePolymerMapTabState<AaSequence, AaSequenceLinearMapState> {
  mapState(options: {polymer: AaSequence; visibilityOptions: VisibilityOptions}): AaSequenceLinearMapState {
    return new AaSequenceLinearMapState(options);
  }

  msVisibilityOptions(): VisibilityOptions {
    const editableOptions = this.get('visibilityOptions')
      .filter((opt) => opt.get('editable'))
      .map((opt) => opt.get('name'));
    return this.get('visibilityOptions').makeLocalSync({editableOptions});
  }

  override defaults(): TabStateDefaults & Partial<AaSequenceMapTabStateAttributes> {
    const showLegacyAntibodyAnnotations =
      BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS && !isBiologicsEnabled();
    const visibilityOptions = _.compact([
      {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
      {base: vo.AA_SEQUENCE_AXIS, visible: true, editable: false, sync: true},
      {base: vo.DNA, visible: true, editable: false, sync: true},
      {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS
        ? {base: vo.PART_ANNOTATIONS, visible: true, editable: true, sync: true}
        : null,
      {base: vo.SEARCH_AMINO_ACIDS, visible: true, editable: false, sync: true},
      {base: vo.AA_SEQUENCE_COLORING, visible: true, editable: true, sync: true},
      {base: vo.PROTEIN_HYDROPHOBICITY_GRAPH, visible: false, editable: true, sync: true},
      {base: vo.PROTEIN_PI_GRAPH, visible: false, editable: true, sync: true},
      showLegacyAntibodyAnnotations
        ? {base: vo.ANTIBODY_NUMBERING, visible: false, editable: true, sync: true}
        : null,
      showLegacyAntibodyAnnotations
        ? {base: vo.ANTIBODY_CDR_ANNOTATIONS, visible: false, editable: true, sync: true}
        : null,
      showLegacyAntibodyAnnotations
        ? {base: vo.PROTEIN_LIABILITY_SITES, visible: false, editable: true, sync: true}
        : null,
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS ? {base: vo.PARTS, visible: true, editable: false} : null,
    ]);
    return {
      ...super.defaults(),
      closeable: false,
      title: 'Sequence Map',
      bufferViewLayout: null,
      visibilityOptions: constructOptions('linear', visibilityOptions),
      // Persisted: FileState.saveLayoutPreference writes this value into
      // `workspaceBuffers.${fileType}` via POST /1/api/users/me/preferences, and
      // loadLayout matches on it to restore a tab. It also feeds the URL `tab`
      // param through activateTabByIdentifier, so shared links carry it too.
      // Renaming orphans saved layouts and breaks those links.
      // This is not the map's DOM id, which AaSequenceMapView sets to
      // 'aa-sequence-map'. The two are independent strings that only look alike.
      preferenceKey: 'protein-map',
    };
  }
}

export default AaSequenceMapTabState;
